// @flow
import { useEffect, useRef, useState } from 'react';
import { parseQueryParams } from '../utils/queryParams';

/**
 * Hook that returns the location's previous value
 */
const usePrevious = (location: any) => {
    const ref = useRef();

    useEffect(() => {
        ref.current = location;
    }, [location]);

    return ref.current;
};

/**
 * Restores scroll position when returning to previously visited routes
 * as well as setting scroll position on new pages to (0,0)
 * @see https://reacttraining.com/react-router/web/guides/scroll-restoration
 */
type ScrollMemoryProps = { currLocation: any, routeIsPreloading: boolean };
const ScrollMemory = ({ currLocation, routeIsPreloading }: ScrollMemoryProps) => {
    const prevLocation = usePrevious(currLocation);
    const [historyScrollLocations, setHistoryScrollLocations] = useState(new Map());

    useEffect(() => {
        if (routeIsPreloading) {
            return;
        }

        // Get saved scroll position for current page
        let historicalLocation = historyScrollLocations.get(currLocation.key);

        if (historicalLocation) {
            window.scrollTo(0, historicalLocation);
        } else if (prevLocation) {
            setHistoryScrollLocations(sl => sl.set(prevLocation.key, window.scrollY));

            if (currLocation.pathname === prevLocation.pathname) {
                let newLocationSearch: any = parseQueryParams(currLocation.search);
                let previousLocationSearch: any = parseQueryParams(prevLocation.search);
                if (
                    newLocationSearch.page !== previousLocationSearch.page ||
                    newLocationSearch.sort !== previousLocationSearch.sort ||
                    newLocationSearch.tab !== previousLocationSearch.tab ||
                    newLocationSearch.folderId !== previousLocationSearch.folderId
                ) {
                    window.scrollTo(0, 0);
                }
            } else {
                window.scrollTo(0, 0);
            }
        }
    }, [currLocation, historyScrollLocations, prevLocation, routeIsPreloading]);

    return null;
};

export default ScrollMemory;
